package com.videoanalyzer.web.components

import androidx.compose.runtime.*
import com.videoanalyzer.web.components.analysis.*
import com.videoanalyzer.web.models.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Analysis results display.
 *
 * Shows:
 * - Overall score
 * - Title analysis with suggestions
 * - Thumbnail analysis with detected elements
 * - Top recommendations
 *
 * Component implementations are in the analysis/ subpackage:
 * - ScoreComponents.kt: Score display components
 * - TitleAnalysisSection.kt: Title analysis cards
 * - ThumbnailAnalysisSection.kt: Thumbnail analysis cards
 * - RecommendationsSection.kt: Quick wins recommendations
 * - DetailedAnalysisCards.kt: Extended analysis sections
 * - AnalysisCardUtilities.kt: Shared utilities
 */
@Composable
fun ResultsCard(
    analysis: FullAnalysis,
    isExisting: Boolean,
    message: String?,
    onNewAnalysis: () -> Unit,
    showFullRecommendations: Boolean = true,
    totalRecommendationsCount: Int = 0,
    freeRecommendationsLimit: Int = 2,
    onUpgrade: () -> Unit = {}
) {
    Div({
        classes("fade-in-up")
        style {
            maxWidth(1000.px)
            margin(0.px, "auto".unsafeCast<CSSNumeric>())
        }
    }) {
        // Existing analysis notice
        if (isExisting && message != null) {
            Div({
                classes("fade-in")
                style {
                    backgroundColor(Color("rgba(10, 132, 255, 0.15)"))
                    color(Color("#FF0000"))
                    padding(14.px, 20.px)
                    borderRadius(8.px)
                    marginBottom(24.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                    property("border", "1px solid rgba(10, 132, 255, 0.3)")
                }
            }) {
                Span({ style { fontSize(20.px) } }) { Text("ℹ️") }
                Text(message)
            }
        }

        // Video info header
        VideoHeader(analysis)

        // Overall score - prominent display
        OverallScoreCard(analysis.overallScore)

        // Score-only mode when gated (not first analysis for free users)
        if (!showFullRecommendations) {
            // Show condensed score summary with upgrade CTA
            GatedAnalysisView(
                analysis = analysis,
                onUpgrade = onUpgrade
            )
        } else {
            // Full analysis view for first analysis or paid users
            // Core Analysis sections grid (Title & Thumbnail)
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(auto-fit, minmax(420px, 1fr))")
                    gap(24.px)
                    marginTop(24.px)
                }
            }) {
                // Title analysis
                TitleAnalysisCard(analysis.titleAnalysis)

                // Thumbnail analysis
                analysis.thumbnailAnalysis?.let {
                    ThumbnailAnalysisCard(it)
                }
            }

            // Extended Analysis sections
            ExtendedAnalysisSections(analysis)

            // Top recommendations
            RecommendationsCard(
                recommendations = analysis.topRecommendations,
                showFull = true,
                freeLimit = freeRecommendationsLimit,
                totalCount = totalRecommendationsCount,
                onUpgrade = onUpgrade
            )
        }

        // Action buttons
        ActionButtons(
            analysisId = analysis.id ?: "",
            overallScore = analysis.overallScore,
            onNewAnalysis = onNewAnalysis
        )
    }
}

@Composable
private fun VideoHeader(analysis: FullAnalysis) {
    Div({
        classes("card", "fade-in-up")
        style {
            display(DisplayStyle.Flex)
            gap(20.px)
            marginBottom(24.px)
            padding(28.px)
            alignItems(AlignItems.Center)
        }
    }) {
        // Thumbnail
        analysis.videoThumbnailUrl?.let { url ->
            Div({
                style {
                    position(Position.Relative)
                    borderRadius(8.px)
                    overflow("hidden")
                    property("flex-shrink", "0")
                }
            }) {
                Img(src = url) {
                    style {
                        width(180.px)
                        height(100.px)
                        property("object-fit", "cover")
                        display(DisplayStyle.Block)
                    }
                }
            }
        }

        // Video info
        Div({
            style { property("flex", "1") }
        }) {
            // YouTube badge
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    marginBottom(8.px)
                }
            }) {
                Span({
                    style {
                        backgroundColor(Color("rgba(255, 0, 0, 0.15)"))
                        color(Color("#E53935"))
                        padding(4.px, 12.px)
                        borderRadius(50.px)
                        fontSize(11.px)
                        fontWeight(600)
                        property("text-transform", "uppercase")
                        letterSpacing(0.5.px)
                    }
                }) { Text("YouTube Video") }
            }

            H2({
                style {
                    fontSize(20.px)
                    fontWeight(700)
                    color(Color("#FAFAFA"))
                    marginBottom(8.px)
                    property("line-height", "1.4")
                }
            }) { Text(analysis.videoTitle) }

            P({
                style {
                    fontSize(13.px)
                    color(Color("#71717A"))
                    fontFamily("'SF Mono', Monaco, monospace")
                }
            }) { Text("ID: ${analysis.videoId}") }
        }
    }
}

@Composable
private fun ActionButtons(
    analysisId: String,
    overallScore: Int,
    onNewAnalysis: () -> Unit
) {
    Div({
        style {
            marginTop(40.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            gap(16.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        // Share button
        Button({
            classes("btn")
            style {
                backgroundColor(Color("rgba(14, 165, 233, 0.15)"))
                color(Color("#FF0000"))
                padding(12.px, 24.px)
                borderRadius(10.px)
                border(1.px, LineStyle.Solid, Color("rgba(14, 165, 233, 0.3)"))
                fontSize(15.px)
                fontWeight(600)
                cursor("pointer")
                property("transition", "all 0.2s ease")
            }
            onClick {
                // Copy share link to clipboard
                val shareUrl = "${kotlinx.browser.window.location.origin}/share/$analysisId"
                kotlinx.browser.window.navigator.clipboard.writeText(shareUrl)
                kotlinx.browser.window.alert("Share link copied! Score: $overallScore/100")
            }
        }) {
            Span({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({ style { fontSize(16.px) } }) { Text("📤") }
                Text("Share Score")
            }
        }

        // Analyze another button
        Button({
            classes("btn", "btn-primary", "btn-large")
            style {
                borderRadius(10.px)
                fontWeight(600)
            }
            onClick { onNewAnalysis() }
        }) {
            Span({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Text("Analyze Another Video")
                Span({ style { fontSize(18.px) } }) { Text("→") }
            }
        }
    }
}
